import { useCallback, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { TransportProvider } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'

import {
  authTransport,
  publicTransport,
} from '@jotta/grpc-connect-client/transport'
import { SubscriptionType } from '@jotta/grpc-connect-openapi/customer'
import type { Offer } from '@jotta/grpc-connect-openapi/offer'
import {
  Currency,
  RecurringSubscriptionInterval,
} from '@jotta/grpc-connect-openapi/payment'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { CookieConsentContext } from '@jotta/ui/CookieConsentModal'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import Spinner from '@jotta/ui/Spinner'
import SvgLogoMain from '@jotta/ui/SvgLogo_mainLight'
import { getEnumKey } from '@jotta/utils/enum'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import { PaymentInformation } from '../payment/PaymentInformation'
import { PaymentLayout } from '../payment/PaymentLayout'
import { useOfferState } from '../payment/useOfferState'

import { BusinessSignup } from './BusinessSignup'
import { CreateUser } from './CreateUser'
import { FreeSignup } from './FreeSignup'
import Step1Svg from './step1.svg'
import Step2Svg from './step2.svg'

export function successUrl(
  offer: Offer,
  interval: RecurringSubscriptionInterval,
  paidSignup: boolean,
) {
  const currency = offer?.yearly?.basePrice?.currency
  const intervalKey = getEnumKey(RecurringSubscriptionInterval, interval)
  const currencyKey = currency && getEnumKey(Currency, currency)
  const yearlyPriceCents = (() => {
    switch (interval) {
      case RecurringSubscriptionInterval.MONTHLY_RECURRING:
        return offer?.monthly?.recurringPrice?.amount !== undefined
          ? offer?.monthly?.recurringPrice?.amount * 12n
          : undefined
      case RecurringSubscriptionInterval.YEARLY_RECURRING:
        return offer?.yearly?.recurringPrice?.amount
      case RecurringSubscriptionInterval.NOT_SET_RECURRING:
        return undefined
    }
  })()

  const successURL = new URL(
    `${location.origin}/web/${paidSignup ? 'signupAccepted' : 'upgradeAccepted'}/${offer.productCode}/${intervalKey}`,
  )
  successURL.searchParams.set('product', offer.productCode)
  if (currencyKey) {
    successURL.searchParams.set('currency', currencyKey)
  }
  if (yearlyPriceCents) {
    successURL.searchParams.set(
      'yearly_price',
      (Number(yearlyPriceCents) / 100).toFixed(2),
    )
  }

  return successURL.href
}

export function PaidSignup({
  productCode,
  defaultSubscriptionInterval,
}: {
  productCode: string
  defaultSubscriptionInterval: RecurringSubscriptionInterval
}) {
  const navigate = useNavigate()
  const gotoBilling = useCallback(() => navigate('/web/billing'), [navigate])
  const offerState = useOfferState(productCode, defaultSubscriptionInterval)
  const {
    type,
    free,
    authenticated,
    offer,
    paymentMethods,
    interval,
    subscriptionType,
    country,
  } = offerState

  useEffect(() => {
    if (
      subscriptionType != SubscriptionType.FREE &&
      subscriptionType != SubscriptionType.UNKNOWN_SUBSCRIPTION_TYPE
    ) {
      gotoBilling()
    }
  }, [subscriptionType, gotoBilling])

  const { requestCookieConsent } = useContext(CookieConsentContext)
  useEffect(requestCookieConsent, [requestCookieConsent])

  if (!type || !offer) {
    return <LoadingOverlay id="PaidSignup" open />
  }

  if (free) {
    if (typeof type !== 'string') {
      throw new Error('paid signup not supported')
    }
    switch (type) {
      case 'private':
      case 'voucher':
        return <FreeSignup allowAppPromotion={false} />
      case 'business':
        return <BusinessSignup />
      default:
        return exhaustiveGuard(type)
    }
  }

  return (
    <PaymentLayout
      backPath="/"
      backElement={<PlainBrandIcon icon={SvgLogoMain} height="38px" />}
      offer={offer}
      interval={interval}
      progressIndicator={authenticated ? <Step2Svg /> : <Step1Svg />}
      title={authenticated ? t`Enter payment details` : t`Create user`}
    >
      {summary =>
        authenticated ? (
          <TransportProvider
            transport={authenticated ? authTransport : publicTransport}
          >
            {offer && paymentMethods ? (
              <PaymentInformation
                displayCountrySelector
                offerState={offerState}
                successUrl={successUrl(offer, interval, true)}
                summary={summary}
              />
            ) : (
              <Spinner />
            )}
          </TransportProvider>
        ) : (
          <CreateUser
            allowAppPromotion={false}
            className="mt-12"
            keepRouteOnSuccess
            type={type}
            ctaLabelText={t`Continue`}
            countryOverride={country}
            federationIntent={{
              case: 'signup',
              value: {
                productCode,
              },
            }}
          />
        )
      }
    </PaymentLayout>
  )
}
